import { useCallback, useEffect, useState } from "react";
import type { PatientNote } from "@ferrivia/api-contract";
import { useApi } from "@ferrivia/auth-web";

export type CustomerNotesState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; notes: PatientNote[] };

// No cache, like useCustomerDetails — the Notes tab is the only reader and
// it is fetched once per visit. `prepend` puts the note a POST just returned
// at the top of the loaded list, so a save shows up without a second GET.
// Prepend, not re-sort: the list is newest-first and the server has just
// stamped this one now.
export function useCustomerNotes(
  id: string | undefined,
): [CustomerNotesState, (note: PatientNote) => void] {
  const api = useApi();
  const [state, setState] = useState<CustomerNotesState>({ status: "loading" });

  useEffect(() => {
    if (!id) {
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    api.patients
      .notes(id)
      .then((notes) => {
        if (!cancelled) {
          setState({ status: "success", notes });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({ status: "error", error: err instanceof Error ? err.message : "Failed to load notes" });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, id]);

  const prepend = useCallback((note: PatientNote) => {
    setState((current) =>
      current.status === "success" ? { status: "success", notes: [note, ...current.notes] } : current,
    );
  }, []);

  return [state, prepend];
}
